import { useTranslation } from "react-i18next";
import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShoppingCart, Star, SlidersHorizontal, ChevronDown, ChevronRight,
  ArrowRight, Search, Heart, X, Truck, RotateCcw, Award, Leaf,
  MessageSquare, Send, Loader2
} from "lucide-react";
import SEO from "./SEO";

const API_BASE = import.meta.env.VITE_API_URL || "https://daroulmesbackend.onrender.com";

const INSPECT_MODAL_RESPONSIVE_CSS = `
@media (max-width: 760px) {
  .inspect-modal-overlay {
    align-items: stretch !important;
    justify-content: flex-start !important;
    padding: max(10px, env(safe-area-inset-top)) 10px max(10px, env(safe-area-inset-bottom)) !important;
    overflow-y: auto !important;
  }
  .inspect-modal-shell {
    width: 100% !important;
    max-width: none !important;
    height: calc(100dvh - 20px) !important;
    max-height: calc(100dvh - 20px) !important;
    border-radius: 16px !important;
    display: flex !important;
    flex-direction: column !important;
  }
  .inspect-modal-header {
    padding: 12px 14px !important;
    gap: 10px !important;
  }
  .inspect-modal-header h3,
  .inspect-modal-header p {
    max-width: 58vw !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    white-space: nowrap !important;
  }
  .inspect-modal-tabs,
  .inspect-modal-controls-row {
    width: 100% !important;
    order: 3 !important;
    justify-content: stretch !important;
  }
  .inspect-modal-tabs button,
  .inspect-modal-controls-row button {
    flex: 1 1 0 !important;
    min-width: 0 !important;
    padding: 8px 8px !important;
    font-size: 10px !important;
  }
  .inspect-modal-body {
    flex: 1 1 auto !important;
    display: flex !important;
    flex-direction: column !important;
    overflow-y: auto !important;
    min-height: 0 !important;
  }
  .inspect-modal-image-panel {
    flex: 0 0 auto !important;
    width: 100% !important;
    min-height: 0 !important;
    height: min(54dvh, 420px) !important;
  }
  .inspect-modal-image-panel > div {
    min-height: 0 !important;
    height: 100% !important;
  }
  .inspect-modal-image-panel img {
    object-fit: contain !important;
  }
  .inspect-modal-side {
    width: 100% !important;
    max-height: none !important;
    flex: 1 1 auto !important;
    border-left: none !important;
    border-top: 1px solid rgba(200,160,101,.18) !important;
    overflow-y: visible !important;
  }
  .inspect-modal-side > div {
    padding: 14px !important;
  }
  .inspect-modal-side [style*="flex-direction:column"] {
    flex-direction: row !important;
    flex-wrap: wrap !important;
  }
  .inspect-mobile-hide {
    display: none !important;
  }
}
@media (max-width: 420px) {
  .inspect-modal-image-panel {
    height: 46dvh !important;
  }
  .inspect-modal-header h3,
  .inspect-modal-header p {
    max-width: 48vw !important;
  }
}
`;
const formatMAD = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "0 MAD";
  return `${amount.toLocaleString("en-MA", { minimumFractionDigits: amount % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 })} MAD`;
};

const TAG_LABELS = { bestseller: "Bestseller", new: "New", sale: "Sale" };
const SORT_LABELS = {
  featured: "Featured",
  price_low_high: "Price: Low to High",
  price_high_low: "Price: High to Low",
  top_rated: "Top Rated",
  newest: "Newest",
};


/* ─── Normalise a raw API product ─── */
function normaliseProduct(p) {
  const meta = {};
  (p.productMetadata || []).forEach(({ key, value }) => { meta[key] = value; });
  return {
    id:      p.id,
    name:    p.name,
    price:   p.price,
    orig:    meta.originalPrice ? parseFloat(meta.originalPrice) : null,
    rating:  Math.round(p.rating ?? 0),
    reviews: parseInt(meta.reviews ?? "0", 10),
    tag:     p.tag || "",
    cat:     p.category?.name || "",
    mat:     meta.material || meta.mat || "",
    img:     p.imageUrl
               ? p.imageUrl.startsWith("http")
                 ? p.imageUrl
                 : `${API_BASE}/api/images/uploads/${p.imageUrl.split("/").pop()}`
               : "https://images.unsplash.com/photo-1585515320310-259814833e62?w=500&q=80",
  };
}

const SORT_OPTS = ["featured", "price_low_high", "price_high_low", "top_rated", "newest"];

/* ─── Skeleton loader ─── */
function SkeletonCard() {
  return (
    <div style={{ background: "#fff", borderRadius: 16, overflow: "hidden", border: "1px solid rgba(200,160,101,.14)", boxShadow: "0 6px 22px rgba(180,140,100,.07)" }}>
      <div style={{ aspectRatio: "1/1.1", background: "linear-gradient(90deg,#f0ebe4 25%,#f9f5f0 50%,#f0ebe4 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.4s infinite" }} />
      <div style={{ padding: "15px 15px 13px", display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ height: 10, width: "60%", borderRadius: 4, background: "#f0ebe4" }} />
        <div style={{ height: 16, width: "85%", borderRadius: 4, background: "#f0ebe4" }} />
        <div style={{ height: 36, width: "100%", borderRadius: 4, background: "#f0ebe4", marginTop: 4 }} />
      </div>
    </div>
  );
}
const SORT_KEYS = ["featured","price_low_high","price_high_low","top_rated","newest"];
const TAG_STYLE  = {
  Bestseller:{ background:"#1f3d3a", color:"#dfcc25" },
  New:       { background:"#c8a065", color:"#fff" },
  Sale:      { background:"#c0533a", color:"#fff" },
};

function Stars({ n=5, size=11 }) {
  return (
    <span style={{display:"inline-flex",gap:2}}>
      {Array.from({length:5}).map((_,i)=>(
        <Star key={i} size={size} fill={i<n?"#c8a065":"#e2d8ca"} color={i<n?"#c8a065":"#e2d8ca"}/>
      ))}
    </span>
  );
}

function AccordionFilter({ title, children, defaultOpen=true }) {
  const [open,setOpen] = useState(defaultOpen);
  return (
    <div style={{borderBottom:"1px solid rgba(200,160,101,.18)",paddingBottom:16,marginBottom:16}}>
      <button onClick={()=>setOpen(o=>!o)} style={{
        width:"100%",display:"flex",alignItems:"center",justifyContent:"space-between",
        background:"transparent",border:"none",cursor:"pointer",padding:"0 0 13px 0",
      }}>
        <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,fontWeight:700,
          letterSpacing:".14em",color:"#1f3d3a"}}>{title}</span>
        <ChevronDown size={13} color="#c8a065"
          style={{transform:open?"rotate(180deg)":"none",transition:"transform .3s"}}/>
      </button>
      {open && <div>{children}</div>}
    </div>
  );
}

/* ─── Interactive Star Input ─── */
function StarInput({ value, onChange, size = 24 }) {
  const [hover, setHover] = useState(0);
  const display = hover || value;
  return (
    <div style={{ display: "inline-flex", gap: 4 }} onMouseLeave={() => setHover(0)}>
      {[1, 2, 3, 4, 5].map((i) => (
        <button
          key={i}
          type="button"
          onClick={() => onChange(i)}
          onMouseEnter={() => setHover(i)}
          style={{
            background: "none", border: "none", cursor: "pointer", padding: 2,
            transition: "transform .15s",
            transform: hover === i ? "scale(1.15)" : "scale(1)",
          }}
          aria-label={`${i} star${i > 1 ? "s" : ""}`}
        >
          <Star
            size={size}
            fill={i <= display ? "#c8a065" : "transparent"}
            color={i <= display ? "#c8a065" : "#d4c5b0"}
            strokeWidth={1.5}
          />
        </button>
      ))}
    </div>
  );
}

/* ─── Reviews Section (fetches + displays + submits) ─── */
function ReviewsSection({ productId }) {
  const { t } = useTranslation();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // form state
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // auth detection — adjust the key if yours differs
  const token =
    (typeof window !== "undefined" &&
      (localStorage.getItem("access_token") ||
        localStorage.getItem("token") ||
        "")) ||
    "";
  const currentUser = (() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  })();

  const loadReviews = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/products/${productId}/reviews`);
      if (!res.ok) throw new Error("Failed to load reviews");
      const data = await res.json();
      setReviews(Array.isArray(data) ? data : []);
      // if the user already reviewed, pre-fill their existing review
      if (currentUser) {
        const mine = data.find((r) => r.userId === currentUser.id);
        if (mine) {
          setRating(mine.rating);
          setComment(mine.comment || "");
        }
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [productId, currentUser?.id]);

  useEffect(() => {
    if (productId) loadReviews();
  }, [productId, loadReviews]);

  const userAlreadyReviewed =
    currentUser && reviews.some((r) => r.userId === currentUser.id);

  const submit = async (e) => {
    e?.preventDefault?.();
    setSubmitError(null);
    setSubmitSuccess(false);
    if (!rating) { setSubmitError(t("reviews.error_rating", "Please pick a rating")); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/reviews`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ productId, rating, comment: comment.trim() || undefined }),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(payload.error || payload.message || `HTTP ${res.status}`);
      }
      setSubmitSuccess(true);
      setComment("");
      setRating(0);
      // refresh the list so the new review appears
      loadReviews();
    } catch (e) {
      setSubmitError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const avg = reviews.length
    ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
    : "0.0";

  const initials = (name) =>
    (name || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        year: "numeric", month: "short", day: "numeric",
      });
    } catch { return ""; }
  };

  return (
    <div style={{ padding: "20px 18px", borderTop: "1px solid rgba(200,160,101,.14)" }}>
      {/* Section header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <MessageSquare size={14} color="#1f3d3a" />
          <p style={{
            fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700,
            letterSpacing: ".12em", color: "#1f3d3a", margin: 0,
          }}>
            {t("reviews.title", "REVIEWS")}
          </p>
        </div>
        {reviews.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <Stars n={Math.round(avg)} size={12} />
            <span style={{
              fontFamily: "'Montserrat',sans-serif", fontSize: 11,
              color: "#7a6a58", letterSpacing: ".05em",
            }}>
              {avg} · {reviews.length}
            </span>
          </div>
        )}
      </div>

      {/* List */}
      {loading && (
        <div style={{ display: "flex", justifyContent: "center", padding: 20, color: "#b0a090" }}>
          <Loader2 size={18} style={{ animation: "spin 1s linear infinite" }} />
        </div>
      )}
      {error && (
        <div style={{
          padding: "10px 12px", borderRadius: 8, background: "#fef2f2", color: "#991b1b",
          fontSize: 12, fontFamily: "'Montserrat',sans-serif",
        }}>
          {error}
        </div>
      )}

      {!loading && !error && reviews.length === 0 && (
        <div style={{
          padding: "18px 16px", textAlign: "center", borderRadius: 10,
          background: "rgba(200,160,101,.06)",
          fontFamily: "'Cormorant Garamond',serif", fontSize: 14, color: "#7a6a58", fontStyle: "italic",
        }}>
          {t("reviews.empty", "No reviews yet. Be the first to share your thoughts.")}
        </div>
      )}

      {!loading && reviews.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12, maxHeight: 240, overflowY: "auto", paddingRight: 4 }}>
          {reviews.map((r) => (
            <div key={r.id} style={{
              padding: "12px 14px", borderRadius: 10,
              background: "#fff", border: "1px solid rgba(200,160,101,.18)",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                <div style={{
                  width: 28, height: 28, borderRadius: "50%",
                  background: "linear-gradient(135deg,#1f3d3a,#2d5550)",
                  color: "#dfcc25",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700,
                }}>
                  {initials(r.user?.name)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontFamily: "'Cormorant Garamond',serif", fontSize: 14, fontWeight: 600,
                    color: "#1f3d3a", lineHeight: 1.2,
                  }}>
                    {r.user?.name || t("reviews.anonymous", "Anonymous")}
                  </div>
                  <div style={{
                    fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: "#b0a090",
                    letterSpacing: ".05em",
                  }}>
                    {formatDate(r.createdAt)}
                  </div>
                </div>
                <Stars n={Math.round(r.rating)} size={11} />
              </div>
              {r.comment && (
                <p style={{
                  margin: 0, fontFamily: "'Cormorant Garamond',serif",
                  fontSize: 14, lineHeight: 1.55, color: "#4a3d2f",
                }}>
                  {r.comment}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Write form */}
      <div style={{ marginTop: 18, paddingTop: 16, borderTop: "1px dashed rgba(200,160,101,.25)" }}>
        {!token && (
          <p style={{
            margin: 0, padding: "12px 14px", borderRadius: 10,
            background: "rgba(31,61,58,.05)",
            fontFamily: "'Cormorant Garamond',serif", fontSize: 14,
            color: "#1f3d3a", fontStyle: "italic", textAlign: "center",
          }}>
            {t("reviews.login_required", "Please log in to write a review.")}
          </p>
        )}

        {token && userAlreadyReviewed && (
          <p style={{
            margin: 0, padding: "12px 14px", borderRadius: 10,
            background: "rgba(200,160,101,.08)",
            fontFamily: "'Cormorant Garamond',serif", fontSize: 14,
            color: "#7a6a58", fontStyle: "italic", textAlign: "center",
          }}>
            {t("reviews.already_reviewed", "You've already reviewed this product. Thank you!")}
          </p>
        )}

        {token && !userAlreadyReviewed && (
          <form onSubmit={submit}>
            <p style={{
              fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700,
              letterSpacing: ".12em", color: "#1f3d3a", margin: "0 0 10px",
            }}>
              {t("reviews.write_title", "SHARE YOUR EXPERIENCE")}
            </p>

            <div style={{ marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{
                fontFamily: "'Montserrat',sans-serif", fontSize: 10,
                color: "#b0a090", letterSpacing: ".1em",
              }}>
                {t("reviews.your_rating", "YOUR RATING")}
              </span>
              <StarInput value={rating} onChange={setRating} size={22} />
            </div>

            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t("reviews.placeholder", "Tell others what you think about this piece…")}
              rows={3}
              maxLength={1000}
              style={{
                width: "100%", boxSizing: "border-box",
                padding: "10px 12px", borderRadius: 10,
                border: "1px solid rgba(200,160,101,.35)",
                background: "#fdfaf5", outline: "none", resize: "vertical",
                fontFamily: "'Cormorant Garamond',serif", fontSize: 14,
                color: "#1f3d3a", lineHeight: 1.5,
              }}
              onFocus={e => e.currentTarget.style.borderColor = "#1f3d3a"}
              onBlur={e => e.currentTarget.style.borderColor = "rgba(200,160,101,.35)"}
            />

            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              marginTop: 10, gap: 10, flexWrap: "wrap",
            }}>
              <span style={{
                fontFamily: "'Montserrat',sans-serif", fontSize: 10,
                color: "#b0a090", letterSpacing: ".05em",
              }}>
                {comment.length}/1000
              </span>
              <button
                type="submit"
                disabled={submitting || !rating}
                style={{
                  padding: "10px 18px", borderRadius: 10,
                  background: submitting || !rating ? "#9ca3a0" : "#1f3d3a",
                  border: "none",
                  fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 600,
                  letterSpacing: ".1em", color: "#fdf8f2",
                  cursor: submitting || !rating ? "not-allowed" : "pointer",
                  display: "inline-flex", alignItems: "center", gap: 8,
                  transition: "background .2s",
                }}
                onMouseEnter={e => { if (!submitting && rating) e.currentTarget.style.background = "#2d5550"; }}
                onMouseLeave={e => { if (!submitting && rating) e.currentTarget.style.background = "#1f3d3a"; }}
              >
                {submitting
                  ? <Loader2 size={13} style={{ animation: "spin 1s linear infinite" }} />
                  : <Send size={13} />}
                {t("reviews.submit", "POST REVIEW")}
              </button>
            </div>

            {submitError && (
              <p style={{
                marginTop: 10, padding: "8px 12px", borderRadius: 8,
                background: "#fef2f2", color: "#991b1b",
                fontFamily: "'Montserrat',sans-serif", fontSize: 11,
              }}>
                {submitError}
              </p>
            )}
            {submitSuccess && (
              <p style={{
                marginTop: 10, padding: "8px 12px", borderRadius: 8,
                background: "#f0fdf4", color: "#166534",
                fontFamily: "'Montserrat',sans-serif", fontSize: 11,
              }}>
                {t("reviews.success", "Thank you for your review!")}
              </p>
            )}
          </form>
        )}
      </div>
    </div>
  );
}

/* ─── Kitchen Zoom Viewer Modal ─── */
function ZoomModal({ product, onClose }) {
  const containerRef = useRef(null);
  const { t } = useTranslation(); 
  const [zoom, setZoom] = useState(1);
  const [pos, setPos] = useState({ x: 50, y: 50 });
  const [isDragging, setIsDragging] = useState(false);
  const [lensPos, setLensPos] = useState({ x: -200, y: -200 });
  const [lensVisible, setLensVisible] = useState(false);
  const [activeTab, setActiveTab] = useState("zoom");
  const dragStart = useRef({ x: 0, y: 0, px: 50, py: 50 });
  const LENS_SIZE = 140;
  const LENS_ZOOM = 3;

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    setZoom(z => Math.min(5, Math.max(1, z - e.deltaY * 0.005)));
  }, []);

  const handleMouseDown = (e) => {
    if (zoom <= 1) return;
    setIsDragging(true);
    dragStart.current = { x: e.clientX, y: e.clientY, px: pos.x, py: pos.y };
  };

  const handleMouseMove = useCallback((e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const lx = e.clientX - rect.left;
    const ly = e.clientY - rect.top;
    setLensPos({ x: lx, y: ly });

    if (isDragging) {
      const dx = ((e.clientX - dragStart.current.x) / rect.width) * 100 / zoom;
      const dy = ((e.clientY - dragStart.current.y) / rect.height) * 100 / zoom;
      setPos({
        x: Math.min(100, Math.max(0, dragStart.current.px - dx)),
        y: Math.min(100, Math.max(0, dragStart.current.py - dy)),
      });
    }
  }, [isDragging, zoom]);

  const handleMouseUp = () => setIsDragging(false);

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [handleMouseMove]);

  const zoomLevels = [1, 1.5, 2, 3, 5];

  return (
    <div className="inspect-modal-overlay" style={{
      position:"fixed",inset:0,zIndex:1000,
      background:"rgba(10,18,15,.82)",
      backdropFilter:"blur(14px)",
      display:"flex",alignItems:"center",justifyContent:"center",
      padding:20,
    }} onClick={e=>e.target===e.currentTarget&&onClose()}>
      
      <style>{INSPECT_MODAL_RESPONSIVE_CSS}</style>
      <div className="inspect-modal-shell" style={{
        background:"#fdf8f2",borderRadius:22,overflow:"hidden",
        width:"min(900px,96vw)",maxHeight:"92vh",
        display:"flex",flexDirection:"column",
        boxShadow:"0 50px 120px rgba(0,0,0,.5)",
        border:"1px solid rgba(200,160,101,.35)",
      }}>
        {/* Header */}
        <div className="inspect-modal-header" style={{
          display:"flex",alignItems:"center",justifyContent:"space-between",
          padding:"18px 26px",
          borderBottom:"1px solid rgba(200,160,101,.18)",
          background:"rgba(255,253,249,.98)",
          flexWrap:"wrap",gap:10,
        }}>
          <div style={{display:"flex",alignItems:"center",gap:12}}>
            <div style={{
              width:36,height:36,borderRadius:"50%",
              background:"linear-gradient(135deg,#1f3d3a,#2d5550)",
              display:"flex",alignItems:"center",justifyContent:"center",
            }}>
              <span style={{fontSize:18}}>🔬</span>
            </div>
            <div>
              <h3 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:20,fontWeight:600,
                color:"#1f3d3a",margin:0,lineHeight:1.2}}>{product.name}</h3>
              <p style={{fontFamily:"'Montserrat',sans-serif",fontSize:11,color:"#9a8a78",margin:0,
                letterSpacing:".1em"}}>{t("zoom.detail_magnifier")}</p>
            </div>
          </div>
          {/* Mode tabs */}
          <div className="inspect-modal-tabs" style={{display:"flex",background:"rgba(31,61,58,.07)",borderRadius:10,padding:4,gap:4}}>
            {[["zoom","🔬 Zoom Pan"],["lens","🔍 Lens"]].map(([mode,label])=>(
              <button key={mode} onClick={()=>setActiveTab(mode)} style={{
                padding:"7px 16px",borderRadius:8,border:"none",cursor:"pointer",
                fontFamily:"'Montserrat',sans-serif",fontSize:11,fontWeight:600,letterSpacing:".06em",
                background:activeTab===mode?"#1f3d3a":"transparent",
                color:activeTab===mode?"#fdf8f2":"#7a6a58",
                transition:"all .2s",
              }}>{label}</button>
            ))}
          </div>
          <button onClick={onClose} style={{
            width:36,height:36,borderRadius:"50%",border:"1.5px solid rgba(192,83,58,.4)",
            background:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",
            transition:"all .2s",flexShrink:0,
          }}
            onMouseEnter={e=>e.currentTarget.style.background="rgba(192,83,58,.12)"}
            onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
            <X size={15} color="#c0533a"/>
          </button>
        </div>

        {/* Viewer area */}
        <div className="inspect-modal-body" style={{display:"flex",flex:1,overflow:"hidden",minHeight:0}}>
          {/* Main image panel */}
          <div className="inspect-modal-image-panel" style={{flex:1,position:"relative",overflow:"hidden",background:"#1a1a1a"}}>
            {activeTab === "zoom" ? (
              <div
                ref={containerRef}
                onWheel={handleWheel}
                onMouseDown={handleMouseDown}
                style={{
                  width:"100%",height:"100%",minHeight:420,overflow:"hidden",
                  cursor: zoom>1 ? (isDragging?"grabbing":"grab") : "zoom-in",
                  position:"relative",userSelect:"none",
                }}
              >
                <img
                  src={product.img}
                  alt={product.name}
                  draggable={false}
                  style={{
                    width:"100%",height:"100%",objectFit:"cover",
                    transformOrigin:`${pos.x}% ${pos.y}%`,
                    transform:`scale(${zoom})`,
                    transition: isDragging?"none":"transform 0.15s ease",
                    display:"block",
                  }}
                />
                <div style={{
                  position:"absolute",bottom:16,left:16,
                  background:"rgba(0,0,0,.55)",borderRadius:8,
                  padding:"6px 14px",backdropFilter:"blur(6px)",
                }}>
                  <span style={{
                    fontFamily:"'Montserrat',sans-serif",fontSize:13,fontWeight:700,
                    color:"#c8a065",letterSpacing:".06em",
                  }}>{zoom.toFixed(1)}×</span>
                </div>
                {zoom > 1 && (
                  <div style={{
                    position:"absolute",
                    left:`${pos.x}%`,top:`${pos.y}%`,
                    transform:"translate(-50%,-50%)",
                    pointerEvents:"none",opacity:0.4,
                  }}>
                    <svg width="24" height="24" viewBox="0 0 24 24">
                      <line x1="12" y1="0" x2="12" y2="24" stroke="#c8a065" strokeWidth="1.5"/>
                      <line x1="0" y1="12" x2="24" y2="12" stroke="#c8a065" strokeWidth="1.5"/>
                      <circle cx="12" cy="12" r="4" fill="none" stroke="#c8a065" strokeWidth="1.5"/>
                    </svg>
                  </div>
                )}
                <div className="inspect-mobile-hide" style={{
                  position:"absolute",bottom:16,right:16,
                  fontFamily:"'Montserrat',sans-serif",fontSize:11,color:"rgba(255,255,255,.6)",
                  letterSpacing:".06em",textAlign:"right",pointerEvents:"none",
                }}>
                  {zoom>1?t("zoom.drag_to_pan"):t("zoom.scroll_to_zoom")}
                </div>
              </div>
            ) : (
              /* Lens Mode */
              <div
                ref={containerRef}
                onMouseMove={()=>setLensVisible(true)}
                onMouseLeave={()=>setLensVisible(false)}
                style={{
                  width:"100%",height:"100%",minHeight:420,
                  position:"relative",overflow:"hidden",cursor:"none",userSelect:"none",
                }}
              >
                <img
                  src={product.img}
                  alt={product.name}
                  draggable={false}
                  style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}}
                />
                {lensVisible && (
                  <div style={{
                    position:"absolute",
                    left: lensPos.x - LENS_SIZE/2,
                    top:  lensPos.y - LENS_SIZE/2,
                    width: LENS_SIZE,
                    height: LENS_SIZE,
                    borderRadius:"50%",
                    overflow:"hidden",
                    border:"3px solid #c8a065",
                    boxShadow:"0 0 0 1px rgba(200,160,101,.4), 0 8px 30px rgba(0,0,0,.5)",
                    pointerEvents:"none",
                    zIndex:10,
                  }}>
                    {containerRef.current && (() => {
                      const rect = containerRef.current.getBoundingClientRect();
                      const bgPosX = LENS_SIZE/2 - lensPos.x * LENS_ZOOM;
                      const bgPosY = LENS_SIZE/2 - lensPos.y * LENS_ZOOM;
                      return (
                        <div style={{
                          position:"absolute",
                          inset:0,
                          backgroundImage:`url(${product.img})`,
                          backgroundSize:`${rect.width * LENS_ZOOM}px ${rect.height * LENS_ZOOM}px`,
                          backgroundPosition:`${bgPosX}px ${bgPosY}px`,
                          backgroundRepeat:"no-repeat",
                        }}/>
                      );
                    })()}
                    {/* Crosshair */}
                    <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",pointerEvents:"none",opacity:0.5}}>
                      <svg width={LENS_SIZE} height={LENS_SIZE} viewBox={`0 0 ${LENS_SIZE} ${LENS_SIZE}`}>
                        <line x1={LENS_SIZE/2} y1="0" x2={LENS_SIZE/2} y2={LENS_SIZE} stroke="#c8a065" strokeWidth="1"/>
                        <line x1="0" y1={LENS_SIZE/2} x2={LENS_SIZE} y2={LENS_SIZE/2} stroke="#c8a065" strokeWidth="1"/>
                        <circle cx={LENS_SIZE/2} cy={LENS_SIZE/2} r={LENS_SIZE/2-2} fill="none" stroke="#c8a065" strokeWidth="1" opacity="0.6"/>
                      </svg>
                    </div>
                  </div>
                )}
                {/* Lens handle */}
                {lensVisible && (
                  <div style={{
                    position:"absolute",
                    left:lensPos.x+LENS_SIZE/2*0.65,top:lensPos.y+LENS_SIZE/2*0.65,
                    width:28,height:5,background:"#c8a065",borderRadius:3,
                    transform:"rotate(45deg)",transformOrigin:"0 50%",
                    pointerEvents:"none",zIndex:11,
                    boxShadow:"0 2px 6px rgba(0,0,0,.4)",
                  }}/>
                )}
                <div className="inspect-mobile-hide" style={{
                  position:"absolute",bottom:16,right:16,
                  fontFamily:"'Montserrat',sans-serif",fontSize:11,color:"rgba(255,255,255,.6)",
                  letterSpacing:".06em",pointerEvents:"none",
                }}>{t("zoom.move_to_inspect", {zoom: LENS_ZOOM})}</div>
              </div>
            )}
          </div>

          {/* Right panel */}
          <div className="inspect-modal-side" style={{
            width:300,flexShrink:0,background:"rgba(255,253,249,.98)",
            borderLeft:"1px solid rgba(200,160,101,.18)",
            display:"flex",flexDirection:"column",overflowY:"auto",
          }}>
            {activeTab === "zoom" && (
              <div style={{padding:"20px 18px",borderBottom:"1px solid rgba(200,160,101,.14)"}}>
                <p style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,fontWeight:700,
                  letterSpacing:".12em",color:"#1f3d3a",marginBottom:14}}>{t("collectionPage.zoom_level")}</p>
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  {zoomLevels.map(z=>(
                    <button key={z} onClick={()=>{setZoom(z);setPos({x:50,y:50});}} style={{
                      padding:"9px 0",borderRadius:8,border:"1.5px solid",
                      borderColor:Math.abs(zoom-z)<0.1?"#1f3d3a":"rgba(200,160,101,.4)",
                      background:Math.abs(zoom-z)<0.1?"#1f3d3a":"transparent",
                      color:Math.abs(zoom-z)<0.1?"#fdf8f2":"#7a6a58",
                      fontFamily:"'Montserrat',sans-serif",fontSize:12,fontWeight:600,
                      cursor:"pointer",transition:"all .2s",
                    }}>
                      {z===1?"Original":`${z}×`}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div style={{padding:"20px 18px"}}>
              <p style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,fontWeight:700,
                letterSpacing:".12em",color:"#1f3d3a",marginBottom:12}}>{t("collectionPage.product")}</p>
              <img src={product.img} alt={product.name}
                style={{width:"100%",borderRadius:10,objectFit:"cover",aspectRatio:"1/1",marginBottom:12}}/>
              <p style={{fontFamily:"'Cormorant Garamond',serif",fontSize:15,fontWeight:500,
                color:"#1f3d3a",margin:"0 0 4px"}}>{product.name}</p>
              <p style={{fontFamily:"'Montserrat',sans-serif",fontSize:11,color:"#c8a065",
                margin:"0 0 4px"}}>{product.mat} · {product.cat}</p>
              <p style={{fontFamily:"'Cormorant Garamond',serif",fontSize:20,fontWeight:600,
                color:"#1f3d3a",margin:0}}>{formatMAD(product.price)}</p>
            </div>

            {/* Reviews */}
            <ReviewsSection productId={product.id} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function KitchenPage() {
  /* ── API state ── */
  const [allProducts,  setAllProducts]  = useState([]);
  const [categories,   setCategories]   = useState(["All"]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [pagination,   setPagination]   = useState({ total: 0 });

  /* ── Filter / sort state ── */
  const [activeCat,  setActiveCat]  = useState("Kitchen");
  const [activeMats, setActiveMats] = useState([]);
  const [maxPrice,   setMaxPrice]   = useState(250);
  const [activeTags, setActiveTags] = useState([]);
  const [sortBy,     setSortBy]     = useState("featured");
  const [search,     setSearch]     = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  /* ── UI state ── */
  const [wishlist,   setWishlist]   = useState([]);
  const [cartAdded,  setCartAdded]  = useState([]);
  const [modalZoom,  setModalZoom]  = useState(null);
  const navigate = useNavigate();
  const { t } = useTranslation(); 
  /* ── Debounce search ── */
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  /* ── Fetch categories once ── */
  useEffect(() => {
    fetch(`${API_BASE}/api/categories`)
      .then(r => r.json())
      .then(data => setCategories(["All", ...data.map(c => c.name)]))
      .catch(() => setCategories(["All"]));
  }, []);

  /* ── Fetch products ── */
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    const params = new URLSearchParams();
    params.set("limit", "100");
    params.set("includeMetadata", "true");
    if (debouncedSearch) params.set("search", debouncedSearch);
    if (maxPrice < 250)  params.set("maxPrice", maxPrice);
    if (activeTags.length === 1) params.set("tag", activeTags[0]);

    fetch(`${API_BASE}/api/products?${params.toString()}`, { signal: controller.signal })
      .then(r => { if (!r.ok) throw new Error(`Server error ${r.status}`); return r.json(); })
      .then(({ data, pagination: pg }) => {
        setAllProducts(data.map(normaliseProduct));
        setPagination(pg);
        setLoading(false);
      })
      .catch(err => {
        if (err.name !== "AbortError") { setError(err.message); setLoading(false); }
      });

    return () => controller.abort();
  }, [debouncedSearch, maxPrice, activeTags]);

  /* ── Derive dynamic filter options ── */
  const MATERIALS = useMemo(() => [...new Set(allProducts.map(p => p.mat).filter(Boolean))].sort(), [allProducts]);

  const toggleMat  = m  => setActiveMats(p=>p.includes(m)?p.filter(x=>x!==m):[...p,m]);
  const toggleTag  = t  => setActiveTags(p=>p.includes(t)?p.filter(x=>x!==t):[...p,t]);
  const toggleWish = id => setWishlist(p=>p.includes(id)?p.filter(x=>x!==id):[...p,id]);
  const addCart    = id => {
    const product = allProducts.find(p => p.id === id);
    if (!product) return;

    const stored = localStorage.getItem("dar_oulmes_cart");
    const cart = stored ? JSON.parse(stored) : [];
    const existing = cart.find(item => item.id === id);
    if (existing) {
      existing.quantity += 1;
    } else {
      cart.push({ id: product.id, name: product.name, price: product.price, img: product.img, tag: product.tag || "", quantity: 1 });
    }
    localStorage.setItem("dar_oulmes_cart", JSON.stringify(cart));
    window.dispatchEvent(new Event("cart-updated"));

    setCartAdded(p=>[...p,id]);
    setTimeout(()=>setCartAdded(p=>p.filter(x=>x!==id)),1400);
  };
  const hasFilters = activeCat!=="All"||activeMats.length||activeTags.length||search||maxPrice<250;
  const clearAll   = ()=>{setActiveCat("All");setActiveMats([]);setActiveTags([]);setMaxPrice(250);setSearch("");};

  const filtered = useMemo(()=>{
    let list = [...allProducts];
    if (activeCat !== "All") list = list.filter(p=>p.cat===activeCat);
    if (activeMats.length)   list = list.filter(p=>activeMats.includes(p.mat));
    if (activeTags.length > 1) list = list.filter(p=>activeTags.includes(p.tag));
    list = list.filter(p=>p.price<=maxPrice);
    if (search) list = list.filter(p=>p.name.toLowerCase().includes(search.toLowerCase()));
    if (sortBy==="price_low_high") list.sort((a,b)=>a.price-b.price);
    if (sortBy==="price_high_low") list.sort((a,b)=>b.price-a.price);
    if (sortBy==="top_rated")          list.sort((a,b)=>b.reviews-a.reviews);
    return list;
  },[allProducts,activeCat,activeMats,activeTags,maxPrice,search,sortBy]);

  return (
    <div className="pt-16" style={{fontFamily:"'Cormorant Garamond',Georgia,serif",minHeight:"100vh",position:"relative",overflowX:"hidden"}}>
      {modalZoom && <ZoomModal product={modalZoom} onClose={()=>setModalZoom(null)}/>}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300&family=Montserrat:wght@300;400;500;600;700&display=swap');
        *{box-sizing:border-box;}
        .kg-deco{position:fixed;inset:0;pointer-events:none;z-index:0;overflow:hidden;}
        .kg-card{background:#fff;border-radius:16px;overflow:hidden;border:1px solid rgba(200,160,101,.14);
          box-shadow:0 6px 22px rgba(180,140,100,.07);
          transition:transform .35s cubic-bezier(.34,1.56,.64,1),box-shadow .35s;
          cursor:pointer;position:relative;}
        .kg-card:hover{transform:translateY(-6px);box-shadow:0 24px 56px rgba(180,140,100,.2);}
        .kg-card:hover .kg-img{transform:scale(1.07);}
        .kg-img{width:100%;height:100%;object-fit:cover;display:block;transition:transform .55s ease;}
        .kg-cat{width:100%;text-align:left;padding:9px 13px;border-radius:8px;border:none;
          font-family:'Montserrat',sans-serif;font-size:11px;font-weight:500;letter-spacing:.05em;
          cursor:pointer;transition:all .22s;background:transparent;color:#7a6a58;display:flex;justify-content:space-between;align-items:center;}
        .kg-cat.active{background:linear-gradient(135deg,rgba(31,61,58,.1),rgba(200,160,101,.14));color:#1f3d3a;font-weight:700;}
        .kg-cat:hover:not(.active){background:rgba(200,160,101,.09);color:#1f3d3a;}
        .kg-mbox{width:15px;height:15px;border-radius:3px;border:1.5px solid rgba(200,160,101,.5);
          display:flex;align-items:center;justify-content:center;transition:all .2s;flex-shrink:0;cursor:pointer;}
        .kg-mbox.on{background:#1f3d3a;border-color:#1f3d3a;}
        .kg-chip{padding:5px 13px;border-radius:999px;border:1.5px solid rgba(200,160,101,.4);
          font-family:'Montserrat',sans-serif;font-size:10px;font-weight:500;letter-spacing:.06em;
          cursor:pointer;transition:all .22s;background:transparent;color:#7a6a58;}
        .kg-chip.active{background:#1f3d3a;border-color:#1f3d3a;color:#fdf8f2;}
        .kg-chip:hover:not(.active){background:rgba(200,160,101,.12);border-color:#c8a065;color:#1f3d3a;}
        .kg-select{font-family:'Montserrat',sans-serif;font-size:11px;color:#1f3d3a;
          border:1.5px solid rgba(200,160,101,.35);border-radius:8px;padding:8px 12px;;cursor:pointer;outline:none;}
        .kg-search{display:flex;align-items:center;gap:10px;padding:10px 16px;
          ;border:1.5px solid rgba(200,160,101,.3);border-radius:10px;transition:border-color .2s,box-shadow .2s;}
        .kg-search:focus-within{border-color:#c8a065;box-shadow:0 0 0 3px rgba(200,160,101,.12);}
        .kg-cart{width:36px;height:36px;border-radius:50%;background:#1f3d3a;border:none;
          display:flex;align-items:center;justify-content:center;cursor:pointer;flex-shrink:0;
          transition:background .2s,transform .2s;}
        .kg-cart:hover{background:#2d5550;transform:scale(1.1);}
        .kg-cart.done{background:#c8a065;}
        .kg-wish{position:absolute;top:11px;right:11px;width:30px;height:30px;border-radius:50%;
          background:rgba(255,253,249,.9);border:1px solid rgba(200,160,101,.3);
          display:flex;align-items:center;justify-content:center;cursor:pointer;transition:transform .2s,background .2s;}
        .kg-wish:hover{transform:scale(1.12);background:#fff;}
        .kg-zoom-btn{position:absolute;bottom:10px;right:10px;
          background:rgba(31,61,58,.82);border:none;color:#c8a065;cursor:pointer;
          font-family:'Montserrat',sans-serif;font-size:9px;font-weight:700;letter-spacing:.1em;
          padding:6px 11px;border-radius:6px;display:flex;align-items:center;gap:5px;
          backdrop-filter:blur(6px);opacity:0;transition:opacity .25s;pointer-events:none;}
        .kg-card:hover .kg-zoom-btn{opacity:1;pointer-events:auto;}
        input[type=range]{width:100%;accent-color:#c8a065;cursor:pointer;}
        .kg-pill{padding:3px 10px;border-radius:999px;background:rgba(200,160,101,.15);
          font-family:'Montserrat',sans-serif;font-size:10px;color:#7a6a58;display:flex;align-items:center;gap:5px;}
        @keyframes fadeUp{from{opacity:0;transform:translateY(18px);}to{opacity:1;transform:translateY(0);}}
        @keyframes spin{from{transform:rotate(0deg);}to{transform:rotate(360deg);}}
        .kg-fu{animation:fadeUp .6s ease both;}
        .kg-grid>.kg-card:nth-child(1){animation:fadeUp .5s .04s ease both;}
        .kg-grid>.kg-card:nth-child(2){animation:fadeUp .5s .09s ease both;}
        .kg-grid>.kg-card:nth-child(3){animation:fadeUp .5s .14s ease both;}
        .kg-grid>.kg-card:nth-child(4){animation:fadeUp .5s .19s ease both;}
        .kg-grid>.kg-card:nth-child(5){animation:fadeUp .5s .24s ease both;}
        .kg-grid>.kg-card:nth-child(6){animation:fadeUp .5s .29s ease both;}
        .kg-grid>.kg-card:nth-child(7){animation:fadeUp .5s .34s ease both;}
        .kg-grid>.kg-card:nth-child(8){animation:fadeUp .5s .39s ease both;}
        .kg-grid>.kg-card:nth-child(9){animation:fadeUp .5s .44s ease both;}
        .kg-grid>.kg-card:nth-child(n+10){animation:fadeUp .5s .49s ease both;}
        .kg-explore{padding:13px 30px;border-radius:6px;background:#c8a065;border:none;
          font-family:'Montserrat',sans-serif;font-size:11px;font-weight:600;letter-spacing:.12em;
          color:#1f3d3a;cursor:pointer;display:flex;align-items:center;gap:8px;
          box-shadow:0 6px 20px rgba(200,160,101,.4);transition:all .3s;white-space:nowrap;}
        .kg-explore:hover{background:#d4ae78;transform:translateY(-1px);}
        .kg-clear-btn{font-family:'Montserrat',sans-serif;font-size:9px;color:#c8a065;
          background:none;border:none;cursor:pointer;letter-spacing:.06em;font-weight:500;padding:0;}
        .kg-reset-btn{width:100%;padding:10px;border:1.5px solid rgba(200,160,101,.4);border-radius:8px;
          background:transparent;font-family:'Montserrat',sans-serif;font-size:10px;font-weight:500;
          letter-spacing:.1em;color:#9a8a78;cursor:pointer;transition:all .25s;margin-top:4px;}
        .kg-reset-btn:hover{border-color:#1f3d3a;color:#1f3d3a;}
        .kg-zoom-btn{position:absolute;bottom:10px;left:50%;transform:translateX(-50%);
          background:rgba(31,61,58,.88);border:1px solid rgba(200,160,101,.5);
          color:#c8a065;font-family:'Montserrat',sans-serif;font-size:9px;font-weight:600;
          letter-spacing:.1em;padding:6px 14px;border-radius:6px;cursor:pointer;
          display:flex;align-items:center;gap:5px;white-space:nowrap;
          opacity:0;transition:opacity .25s;pointer-events:none;}
        .kg-card:hover .kg-zoom-btn{opacity:1;pointer-events:auto;}

        /* ─── Responsive Styles ─── */
        @media (max-width: 768px) {
          .kg-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 16px; }
          .sidebar-container { grid-column: 1 / -1; margin-bottom: 24px; }
          .main-content { grid-column: 1 / -1; }
          .kg-explore { white-space: normal; text-align: center; }
          .trust-strip { grid-template-columns: 1fr 1fr; }
          .bottom-banner { flex-direction: column; text-align: center; padding: 28px 24px; }
          .breadcrumb { flex-wrap: wrap; }
          h1 { font-size: clamp(32px, 6vw, 48px) !important; }
        }

        @media (max-width: 480px) {
          .kg-grid { grid-template-columns: 1fr !important; }
          .trust-strip { grid-template-columns: 1fr; }
          .kg-card h4 { font-size: 18px !important; }
          .kg-card .price-current { font-size: 24px !important; }
          .sort-bar { flex-direction: column; align-items: flex-start; gap: 12px; }
          .kg-search { max-width: 100%; }
        }

        /* Improved card typography */
        .kg-card .product-name {
          font-family: 'Cormorant Garamond', serif;
          font-size: 16px;
          font-weight: 500;
          color: #1f3d3a;
          line-height: 1.35;
          margin: 0;
        }
        .kg-card .category-tag {
          font-family: 'Montserrat', sans-serif;
          font-size: 9px;
          font-weight: 500;
          color: #c8a065;
          letter-spacing: 0.08em;
        }
        .kg-card .price-current {
          font-family: 'Cormorant Garamond', serif;
          font-size: 22px;
          font-weight: 600;
          color: #1f3d3a;
        }
        .kg-card .price-original {
          font-family: 'Montserrat', sans-serif;
          font-size: 11px;
          color: #b0a090;
          text-decoration: line-through;
        }
        .kg-card .reviews {
          font-family: 'Montserrat', sans-serif;
          font-size: 10px;
          color: #9a8a78;
        }
      `}</style>
      <SEO
        title={t("collectionPage.seo_kitchen")}
        description="Find useful kitchen products and home essentials in Oulmès. Simple local shopping with reasonable prices from Dar Oulmès."
        canonical="/kitchen"
        image="/seo/kitchen.jpg"
      />
      {/* Deco gold lines */}
      <div className="kg-deco">
        <svg width="100%" height="100%" viewBox="0 0 1200 1900" preserveAspectRatio="xMinYMin slice">
          {[0,28,56,84,112,140,168].map((r,i)=>(
            <line key={`tl${i}`} x1="0" y1="0"
              x2={460*Math.cos(r*Math.PI/180)} y2={460*Math.sin(r*Math.PI/180)}
              stroke="#c8a065" strokeWidth=".7" strokeOpacity=".22"/>
          ))}
          {[0,28,56,84,112,140,168].map((r,i)=>(
            <line key={`br${i}`} x1="1200" y1="1900"
              x2={1200-460*Math.cos(r*Math.PI/180)} y2={1900-460*Math.sin(r*Math.PI/180)}
              stroke="#c8a065" strokeWidth=".7" strokeOpacity=".22"/>
          ))}
          {[[1100,260],[1150,380],[1060,140],[75,1650],[35,1790],[105,1720]].map(([cx,cy],i)=>(
            <circle key={`d${i}`} cx={cx} cy={cy} r="3.5" fill="#c8a065" opacity=".28"/>
          ))}
        </svg>
      </div>

      <div style={{position:"relative",zIndex:1,maxWidth:1220,margin:"0 auto",padding:"0 28px 90px"}}>

        {/* Breadcrumb */}
        <div className="breadcrumb" style={{display:"flex",alignItems:"center",gap:6,padding:"28px 0 20px",
          fontFamily:"'Montserrat',sans-serif",fontSize:11,color:"#9a8a78",letterSpacing:".04em"}}>
          {["Home","Collection","Kitchen"].map((c,i,a)=>(
            <React.Fragment key={c}>
              <span style={{cursor:i<a.length-1?"pointer":"default",color:i===a.length-1?"#1f3d3a":"#9a8a78"}}>{c}</span>
              {i<a.length-1&&<ChevronRight size={11} color="#bbb"/>}
            </React.Fragment>
          ))}
        </div>

        {/* Hero heading */}
        <div className="kg-fu" style={{marginBottom:14}}>
          <h1 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:"clamp(36px,4.5vw,58px)",
            fontWeight:400,color:"#1f3d3a",lineHeight:1.15,marginBottom:10}}>
            {t("collectionPage.title_kitchen")}
          </h1>
          <p style={{fontFamily:"'Montserrat',sans-serif",fontSize:12,fontWeight:300,
            color:"#7a6a58",letterSpacing:".04em",marginBottom:28}}>
            {pagination.total || allProducts.length} curated pieces — handcrafted cookware, artisan drinkware &amp; timeless table essentials
          </p>
        </div>

        {/* Search */}
        <div className="kg-fu kg-search" style={{maxWidth:500,marginBottom:36}}>
          <Search size={15} color="#c8a065" style={{flexShrink:0}}/>
          <input value={search} onChange={e=>setSearch(e.target.value)}
            placeholder={"Search kitchen products..."}
            style={{flex:1,border:"none",outline:"none",background:"transparent",
              fontFamily:"'Montserrat',sans-serif",fontSize:12,color:"#1f3d3a"}}/>
          {search&&(
            <button onClick={()=>setSearch("")}
              style={{background:"none",border:"none",cursor:"pointer",padding:0,display:"flex"}}>
              <X size={13} color="#9a8a78"/>
            </button>
          )}
        </div>

        {/* Sidebar + Grid */}
        <div style={{display:"grid",gridTemplateColumns:"240px 1fr",gap:36,alignItems:"start"}}>

          {/* ── SIDEBAR ── */}
          <aside className="sidebar-container kg-fu" style={{
            background:"rgba(255,253,249,.94)",border:"1px solid rgba(200,160,101,.22)",
            borderRadius:16,padding:"26px 20px",
            boxShadow:"0 8px 36px rgba(180,140,100,.09)",
            position:"sticky",top:24,
          }}>
            {/* Filter header */}
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:24}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <SlidersHorizontal size={14} color="#c8a065"/>
                <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,fontWeight:700,
                  letterSpacing:".14em",color:"#1f3d3a"}}>{t("collectionPage.filters_title")}</span>
              </div>
              {hasFilters&&(
                <button className="kg-clear-btn" onClick={clearAll}>{"CLEAR ALL"}</button>
              )}
            </div>

            {/* Category */}
            <AccordionFilter title={t("filters.category")}>
              {categories.map(c=>(
                <button key={c} className={`kg-cat${activeCat===c?" active":""}`}
                  onClick={()=>setActiveCat(c)}>
                  <span>{c}</span>
                  <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,color:"#c8a065",fontWeight:400}}>
                    {c==="All"?pagination.total || allProducts.length:allProducts.filter(p=>p.cat===c).length}
                  </span>
                </button>
              ))}
            </AccordionFilter>

            {/* Price */}
            <AccordionFilter title={t("filters.price_range")}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:10,
                fontFamily:"'Montserrat',sans-serif",fontSize:11,color:"#7a6a58"}}>
                <span>{formatMAD(0)}</span>
                <span style={{color:"#1f3d3a",fontWeight:600}}>{formatMAD(maxPrice)}</span>
              </div>
              <input type="range" min={20} max={250} value={maxPrice}
                onChange={e=>setMaxPrice(+e.target.value)}/>
              <div style={{display:"flex",justifyContent:"space-between",marginTop:6,
                fontFamily:"'Montserrat',sans-serif",fontSize:9,color:"#b0a090"}}>
                <span>{formatMAD(20)}</span><span>{formatMAD(250)}</span>
              </div>
            </AccordionFilter>

            {/* Material */}
            <AccordionFilter title={t("filters.material")}>
              {MATERIALS.map(m=>(
                <label key={m} style={{display:"flex",alignItems:"center",gap:10,marginBottom:11,cursor:"pointer"}}>
                  <div className={`kg-mbox${activeMats.includes(m)?" on":""}`} onClick={()=>toggleMat(m)}>
                    {activeMats.includes(m)&&<span style={{color:"#fdf8f2",fontSize:9,fontWeight:700}}>✓</span>}
                  </div>
                  <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:11,fontWeight:300,color:"#5a4a38",flex:1}}>
                    {m}
                  </span>
                  <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,color:"#c8a065"}}>
                    {allProducts.filter(p=>p.mat===m).length}
                  </span>
                </label>
              ))}
            </AccordionFilter>

            {/* Collection */}
            <AccordionFilter title={t("filters.collection")}>
              <div style={{display:"flex",flexWrap:"wrap",gap:7}}>
                {["bestseller","new","sale"].map(tagKey=>(
                  <button key={tagKey} className={`kg-chip${activeTags.includes(tagKey)?" active":""}`}
                    onClick={()=>toggleTag(tagKey)}>{TAG_LABELS[tagKey]}</button>
                ))}
              </div>
            </AccordionFilter>

            {/* Decorative quote */}
            <div style={{marginTop:4,padding:"16px",background:"rgba(200,160,101,.07)",
              borderRadius:10,borderLeft:"3px solid rgba(200,160,101,.4)"}}>
              <p style={{fontFamily:"'Cormorant Garamond',serif",fontStyle:"italic",
                fontSize:13,color:"#7a6a58",lineHeight:1.7,margin:0}}>
                {t("collectionPage.kitchen_quote")}
              </p>
            </div>

            <button className="kg-reset-btn" onClick={clearAll}>{"RESET ALL"}</button>
          </aside>

          {/* ── PRODUCT GRID ── */}
          <div className="main-content">
            {/* Sort bar */}
            <div className="sort-bar" style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:18,flexWrap:"wrap",gap:12}}>
              <p style={{fontFamily:"'Montserrat',sans-serif",fontSize:11,color:"#7a6a58",fontWeight:300,margin:0}}>
                {t("collectionPage.showing_products", { count: filtered.length, total: pagination.total || allProducts.length })}
              </p>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,color:"#9a8a78",letterSpacing:".1em"}}>{t("collectionPage.sort_by")}</span>
                <select className="kg-select" value={sortBy} onChange={e=>setSortBy(e.target.value)}>
                  {SORT_OPTS.map(o=><option key={o} value={o}>{SORT_LABELS[o] || o}</option>)}
                </select>
              </div>
            </div>

            {/* Active pills */}
            {hasFilters&&(
              <div style={{display:"flex",flexWrap:"wrap",gap:7,marginBottom:18}}>
                {activeCat!=="All"&&(
                  <span className="kg-pill" style={{background:"rgba(31,61,58,.1)",color:"#1f3d3a"}}>
                    {activeCat}<X size={10} style={{cursor:"pointer"}} onClick={()=>setActiveCat("All")}/>
                  </span>
                )}
                {activeMats.map(m=>(
                  <span key={m} className="kg-pill">
                    {m}<X size={10} style={{cursor:"pointer"}} onClick={()=>toggleMat(m)}/>
                  </span>
                ))}
                {activeTags.map(t=>(
                  <span key={t} className="kg-pill">
                    {t}<X size={10} style={{cursor:"pointer"}} onClick={()=>toggleTag(t)}/>
                  </span>
                ))}
                {maxPrice<250&&(
                  <span className="kg-pill">
                    Under {formatMAD(maxPrice)}<X size={10} style={{cursor:"pointer"}} onClick={()=>setMaxPrice(250)}/>
                  </span>
                )}
                {search&&(
                  <span className="kg-pill">
                    &ldquo;{search}&rdquo;<X size={10} style={{cursor:"pointer"}} onClick={()=>setSearch("")}/>
                  </span>
                )}
              </div>
            )}

            {/* Grid */}
            {filtered.length===0?(
              <div style={{textAlign:"center",padding:"90px 0",fontFamily:"'Cormorant Garamond',serif",
                fontSize:24,color:"#9a8a78",fontStyle:"italic"}}>
                {"No products found."}
              </div>
            ):(
              <div className="kg-grid" style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:20}}>
                {filtered.map(p=>(
                  <div key={p.id} className="kg-card" onClick={() => navigate(`/product/${p.id}`)} style={{cursor:"pointer"}}>
                    {/* Image */}
                    <div style={{position:"relative",aspectRatio:"1/1.1",overflow:"hidden",background:"#f5ede3"}}>
                      <img src={p.img} alt={p.name} className="kg-img"/>
                      {p.tag&&(
                        <span style={{
                          position:"absolute",top:12,left:12,...TAG_STYLE[p.tag],
                          fontFamily:"'Montserrat',sans-serif",fontSize:9,fontWeight:700,
                          letterSpacing:".1em",padding:"4px 10px",borderRadius:3,
                        }}>{p.tag}</span>
                      )}
                      {p.orig&&(
                        <span style={{
                          position:"absolute",bottom:12,left:12,
                          background:"rgba(192,83,58,.92)",color:"#fff",
                          fontFamily:"'Montserrat',sans-serif",fontSize:9,fontWeight:700,
                          padding:"3px 8px",borderRadius:3,
                        }}>-{Math.round((1-p.price/p.orig)*100)}%</span>
                      )}
                      <button className="kg-wish" onClick={e=>{e.stopPropagation();toggleWish(p.id);}}>
                        <Heart size={13}
                          fill={wishlist.includes(p.id)?"#c0533a":"none"}
                          color={wishlist.includes(p.id)?"#c0533a":"#9a8a78"}/>
                      </button>
                      <button className="kg-zoom-btn" onClick={e=>{e.stopPropagation();setModalZoom(p);}}>
                        <span style={{fontSize:12}}>🔬</span> {t("collectionPage.inspect_detail")}
                      </button>
                    </div>
                    {/* Info */}
                    <div style={{padding:"15px 15px 13px",display:"flex",flexDirection:"column",gap:5}}>
                      <span className="category-tag">{p.mat} · {p.cat}</span>
                      <h4 className="product-name">{p.name}</h4>
                      <div style={{display:"flex",alignItems:"center",gap:6}}>
                        <Stars n={p.rating}/>
                        <span className="reviews">({p.reviews})</span>
                      </div>
                      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:4}}>
                        <div style={{display:"flex",alignItems:"baseline",gap:7}}>
                          <span className="price-current">{formatMAD(p.price)}</span>
                          {p.orig&&(
                            <span className="price-original">{formatMAD(p.orig)}</span>
                          )}
                        </div>
                        <button className={`kg-cart${cartAdded.includes(p.id)?" done":""}`}
                          onClick={e=>{e.stopPropagation();addCart(p.id);}}>
                          {cartAdded.includes(p.id)
                            ?<span style={{fontSize:14,color:"#fff",lineHeight:1}}>✓</span>
                            :<ShoppingCart size={14} color="#fdf8f2"/>}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Trust strip */}
        <div className="trust-strip" style={{
          marginTop:72,display:"grid",gridTemplateColumns:"repeat(4,1fr)",
          background:"rgba(255,253,249,.94)",border:"1px solid rgba(200,160,101,.18)",
          borderRadius:16,overflow:"hidden",boxShadow:"0 6px 30px rgba(180,140,100,.07)",
        }}>
          {[
            {Icon:Award,     title:t("features.handcrafted"),       desc:t("features.handcrafted_desc")},
            {Icon:Leaf,      title:"NATURAL MATERIALS", desc:"Sustainably sourced\nwood, ceramic & copper"},
            {Icon:Truck,     title:t("features.free_delivery"),     desc:"On all orders\nover 150 MAD"},
            {Icon:RotateCcw, title:t("features.easy_returns"),      desc:t("features.easy_returns_desc")},
          ].map(({Icon,title,desc},i,a)=>(
            <div key={title} style={{
              display:"flex",alignItems:"center",gap:14,padding:"26px 18px",
              borderRight:i<a.length-1?"1px solid rgba(200,160,101,.18)":"none",
            }}>
              <div style={{width:46,height:46,borderRadius:"50%",border:"1.5px solid rgba(200,160,101,.45)",
                display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                <Icon size={18} color="#c8a065"/>
              </div>
              <div>
                <p style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,fontWeight:700,
                  letterSpacing:".12em",color:"#1f3d3a",marginBottom:4}}>{title}</p>
                <p style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,fontWeight:300,
                  color:"#7a6a58",lineHeight:1.7,whiteSpace:"pre-line",margin:0}}>{desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom banner */}
        <div className="bottom-banner" style={{
          marginTop:28,background:"#1f3d3a",borderRadius:14,padding:"36px 44px",
          display:"flex",alignItems:"center",justifyContent:"space-between",gap:32,
        }}>
          <div style={{display:"flex",alignItems:"center",gap:24}}>
            <div style={{width:56,height:56,borderRadius:"50%",border:"1.5px solid rgba(200,160,101,.5)",
              display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,fontSize:28}}>🏠</div>
            <div>
              <h3 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:26,fontWeight:500,
                color:"#fdf8f2",marginBottom:8,lineHeight:1.2}}>{t("collectionPage.a_better_way")}</h3>
              <p style={{fontFamily:"'Montserrat',sans-serif",fontSize:11,fontWeight:300,
                color:"rgba(253,248,242,.65)",lineHeight:1.8,maxWidth:400,margin:0}}>
                {t("collectionPage.banner_sub")}
              </p>
            </div>
          </div>
          <button className="kg-explore">{t("features.explore_collection")} <ArrowRight size={14}/></button>
        </div>

      </div>
    </div>
  );
}